import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BET_LINES, BET_CATEGORIES, type BetCategory, type BetType } from "@/lib/constants";
import { useCouponCart } from "@/stores/couponCart";
import { cn } from "@/lib/utils";
import type { Match, BetSelection, OneXTwo } from "@/types/domain";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  match: Match;
}

/**
 * Sheet de sélection du marché de pari.
 *
 * Trois onglets de niveau supérieur (Vainqueur / Buts / Stats), chacun
 * contenant plusieurs sections — un market par section. Les sections sont
 * scrollables verticalement dans le sheet.
 */
export function BetTypeSheet({ open, onOpenChange, match }: Props) {
  const { addBet } = useCouponCart();
  const [cat, setCat] = useState<BetCategory>("winner");

  function pick(bet: BetSelection) {
    addBet(match, bet);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {match.home.name} <span className="text-muted-foreground font-normal">vs</span> {match.away.name}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={cat} onValueChange={(v) => setCat(v as BetCategory)} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3 shrink-0">
            {(Object.keys(BET_CATEGORIES) as BetCategory[]).map((k) => (
              <TabsTrigger key={k} value={k} className="text-xs">
                {BET_CATEGORIES[k].label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-3">
            <TabsContent value="winner" className="space-y-4 mt-0">
              <Section title="1X2">
                <Grid cols={3}>
                  <Pick label="1" hint={match.home.name} onClick={() => pick({ type: "result", pick: "1" })} />
                  <Pick label="X" hint="Nul" onClick={() => pick({ type: "result", pick: "X" })} />
                  <Pick label="2" hint={match.away.name} onClick={() => pick({ type: "result", pick: "2" })} />
                </Grid>
              </Section>

              <Section title="Double chance">
                <Grid cols={3}>
                  <Pick label="1X" hint={`${match.home.name} ou nul`} onClick={() => pick({ type: "doubleChance", pick: "1X" })} />
                  <Pick label="X2" hint={`Nul ou ${match.away.name}`} onClick={() => pick({ type: "doubleChance", pick: "X2" })} />
                  <Pick label="12" hint="Pas de nul" onClick={() => pick({ type: "doubleChance", pick: "12" })} />
                </Grid>
              </Section>

              <Section title="Draw No Bet" hint="Remboursé si match nul">
                <Grid cols={2}>
                  <Pick label="1" hint={match.home.name} onClick={() => pick({ type: "dnb", pick: "1" })} />
                  <Pick label="2" hint={match.away.name} onClick={() => pick({ type: "dnb", pick: "2" })} />
                </Grid>
              </Section>

              <Section title="1X2 mi-temps">
                <Grid cols={3}>
                  <Pick label="1" hint="Domicile" onClick={() => pick({ type: "htResult", pick: "1" })} />
                  <Pick label="X" hint="Nul" onClick={() => pick({ type: "htResult", pick: "X" })} />
                  <Pick label="2" hint="Extérieur" onClick={() => pick({ type: "htResult", pick: "2" })} />
                </Grid>
              </Section>

              <Section title="Mi-temps / Fin de match" hint="HT puis FT — les 2 doivent passer">
                <HtFtMatrix onPick={(ht, ft) => pick({ type: "htft", ht, ft })} />
              </Section>
            </TabsContent>

            <TabsContent value="goals" className="space-y-4 mt-0">
              <Section title="Score exact">
                <ScoreInputs onSubmit={(home, away) => pick({ type: "score", home, away })} />
              </Section>

              <Section title="+/- buts">
                <OverUnderGrid
                  lines={BET_LINES.goals!}
                  onPick={(line, p) => pick({ type: "goals", line, pick: p })}
                />
              </Section>

              <Section title="Les deux équipes marquent">
                <Grid cols={2}>
                  <Pick label="Oui" hint="Les 2 marquent" onClick={() => pick({ type: "btts", pick: "yes" })} />
                  <Pick label="Non" hint="L'un ne marque pas" onClick={() => pick({ type: "btts", pick: "no" })} />
                </Grid>
              </Section>

              <Section title="+/- buts mi-temps">
                <OverUnderGrid
                  lines={BET_LINES.htGoals!}
                  onPick={(line, p) => pick({ type: "htGoals", line, pick: p })}
                />
              </Section>

              <Section title="+/- buts par équipe">
                <TeamGoalsPicker
                  homeName={match.home.name}
                  awayName={match.away.name}
                  lines={BET_LINES.teamGoals!}
                  onPick={(team, line, p) => pick({ type: "teamGoals", team, line, pick: p })}
                />
              </Section>
            </TabsContent>

            <TabsContent value="stats" className="space-y-4 mt-0">
              <Section title="Corners (total match)">
                <OverUnderGrid
                  lines={BET_LINES.corners!}
                  onPick={(line, p) => pick({ type: "corners", line, pick: p })}
                />
              </Section>

              <Section title="Cartons (jaunes + rouges)">
                <OverUnderGrid
                  lines={BET_LINES.cards!}
                  onPick={(line, p) => pick({ type: "cards", line, pick: p })}
                />
              </Section>

              <Section title="Tirs (total match)">
                <OverUnderGrid
                  lines={BET_LINES.shots!}
                  onPick={(line, p) => pick({ type: "shots", line, pick: p })}
                />
              </Section>

              <Section title="Fautes (total match)">
                <OverUnderGrid
                  lines={BET_LINES.fouls!}
                  onPick={(line, p) => pick({ type: "fouls", line, pick: p })}
                />
              </Section>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">{title}</h3>
        {hint && <span className="text-2xs text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function Grid({ cols, children }: { cols: 2 | 3 | 4; children: React.ReactNode }) {
  return <div className={cn("grid gap-2", cols === 2 && "grid-cols-2", cols === 3 && "grid-cols-3", cols === 4 && "grid-cols-4")}>{children}</div>;
}

function Pick({ label, hint, onClick }: { label: string; hint?: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-col items-center justify-center rounded-xl border border-white/[0.06] bg-card p-3 gap-1 transition-all",
        "hover:border-primary/40 hover:bg-primary/5 active:scale-[0.98]",
      )}
    >
      <span className="text-base font-bold">{label}</span>
      {hint ? <span className="text-2xs text-muted-foreground line-clamp-1">{hint}</span> : null}
    </button>
  );
}

function OverUnderGrid({ lines, onPick }: { lines: readonly number[]; onPick: (line: number, pick: "over" | "under") => void }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {lines.flatMap((line) => [
        <Pick key={`o${line}`} label={`+ ${line}`} hint="Plus" onClick={() => onPick(line, "over")} />,
        <Pick key={`u${line}`} label={`- ${line}`} hint="Moins" onClick={() => onPick(line, "under")} />,
      ])}
    </div>
  );
}

function ScoreInputs({ onSubmit }: { onSubmit: (home: number, away: number) => void }) {
  const [home, setHome] = useState(1);
  const [away, setAway] = useState(0);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <ScoreStepper value={home} onChange={setHome} label="Domicile" />
        <ScoreStepper value={away} onChange={setAway} label="Extérieur" />
      </div>
      <Button onClick={() => onSubmit(home, away)} className="w-full">
        Valider {home}-{away}
      </Button>
    </div>
  );
}

function ScoreStepper({ value, onChange, label }: { value: number; onChange: (n: number) => void; label: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground text-center">{label}</p>
      <div className="flex items-center gap-2">
        <Button size="icon" variant="outline" onClick={() => onChange(Math.max(0, value - 1))}>-</Button>
        <Input
          type="number"
          min={0}
          max={20}
          value={value}
          onChange={(e) => onChange(Math.max(0, Math.min(20, Number(e.target.value))))}
          className="text-center text-xl font-bold"
        />
        <Button size="icon" variant="outline" onClick={() => onChange(Math.min(20, value + 1))}>+</Button>
      </div>
    </div>
  );
}

function HtFtMatrix({ onPick }: { onPick: (ht: OneXTwo, ft: OneXTwo) => void }) {
  const picks: OneXTwo[] = ["1", "X", "2"];
  return (
    <div className="grid grid-cols-3 gap-2">
      {picks.flatMap((ht) =>
        picks.map((ft) => (
          <Pick
            key={`${ht}/${ft}`}
            label={`${ht}/${ft}`}
            hint={`HT ${ht} · FT ${ft}`}
            onClick={() => onPick(ht, ft)}
          />
        )),
      )}
    </div>
  );
}

function TeamGoalsPicker({
  homeName,
  awayName,
  lines,
  onPick,
}: {
  homeName: string;
  awayName: string;
  lines: readonly number[];
  onPick: (team: "home" | "away", line: number, pick: "over" | "under") => void;
}) {
  const [team, setTeam] = useState<"home" | "away">("home");
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => setTeam("home")}
          className={cn(
            "rounded-lg border px-3 py-2 text-xs font-medium transition-colors",
            team === "home"
              ? "border-primary bg-primary/10 text-primary"
              : "border-white/[0.06] text-muted-foreground hover:text-foreground",
          )}
        >
          {homeName}
        </button>
        <button
          onClick={() => setTeam("away")}
          className={cn(
            "rounded-lg border px-3 py-2 text-xs font-medium transition-colors",
            team === "away"
              ? "border-primary bg-primary/10 text-primary"
              : "border-white/[0.06] text-muted-foreground hover:text-foreground",
          )}
        >
          {awayName}
        </button>
      </div>
      <OverUnderGrid lines={lines} onPick={(line, p) => onPick(team, line, p)} />
    </div>
  );
}

// Garde-fou : référence le type BetType utilisé indirectement via les BET_LINES.
// Évite un warning ESLint sur l'import inutilisé si un jour on retire un usage.
export type { BetType };
